import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { createPlotlyChart } from 'src/app/_shared/plotlyConfig';
import { RbUiComponentsModule } from '@inst-iot/bosch-angular-ui-components';
import { VehicleChargingFigureService } from 'src/app/_services/vehicle-charging-figure.service';
import { TransformXAxisPipe } from 'src/app/_shared/pipes/transform-xaxis.pipe';

@Component({
  selector: 'app-end-state-of-charge-ef-chart',
  standalone: true,
  imports: [CommonModule, RbUiComponentsModule],
  templateUrl: './end-state-of-charge-ef-chart.component.html',
  styleUrls: ['./end-state-of-charge-ef-chart.component.scss']
})
export class EndStateOfChargeEfChartComponent implements OnChanges, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() selectedVehicles: any[] = [];
  @Input() dataLoadingSoc: boolean = false;
  @Input() errorMessageSoc: boolean = false;
  @Input() stateOfChargeDatapoints: any = {};
  @Input() fleetId: string[] = [];
  private colorMap: { [key: string]: string } = {};
  chartRange!: { range: [number, number] };

  constructor(
    private readonly vehicleChargingFigureService: VehicleChargingFigureService,
  ) { }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.dataLoadingSoc = true;
    this.errorMessageSoc = false;
    if (Object.keys(this.stateOfChargeDatapoints).length !== 0) {
      const fleetData = this.plotFleetEndSoc();
      this.fetchVehicleInfoAndUpdateChart(fleetData);
    } else {
      this.dataLoadingSoc = false;
      this.errorMessageSoc = true;
    }

  }

  fetchVehicleInfoAndUpdateChart(fleetData: any[]): void {
    const deviceSerialNumber: string[] = this.selectedVehicles.map(vehicle => vehicle.batteryIdentifier);
    const vehicleNameMap: { [key: string]: string } = {};

    this.selectedVehicles.forEach(vehicle => {
      vehicleNameMap[vehicle.batteryIdentifier] = vehicle.name;
    });

    this.vehicleChargingFigureService.getStateOfCharge(deviceSerialNumber).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (data: any) => {
        this.initializeColorMap();

        const fleetHasData = fleetData.some(fd => Array.isArray(fd.y) && fd.y.length > 0);

        let vehicleHasData = false;
        const vehiclePlotData = Object.keys(data).flatMap((deviceSerialNumberId: string) => {
          const deviceDataArray = data[deviceSerialNumberId]
          const vehicleName = vehicleNameMap[deviceSerialNumberId];
          const filteredData = deviceDataArray.filter((dataObject: any) => dataObject.chargingSessionsEnd !== null);
          const bin = filteredData.map((dataObject: any) => dataObject.binSoc);
          const socEnd = filteredData.map((dataObject: any) => dataObject.chargingSessionsEnd);

          if (socEnd.length > 0) {
            vehicleHasData = true;
          }

          return {
            x: bin,
            y: socEnd,
            type: 'bar',
            name: vehicleName,
            marker: {
              color: this.colorMap[deviceSerialNumberId],
            }
          };
        });

        if (!fleetHasData && !vehicleHasData) {
          this.errorMessageSoc = true;
          this.dataLoadingSoc = false;
          return;
        } else {
          this.errorMessageSoc = false;
        }

        const combinedPlotData = [...fleetData, ...vehiclePlotData];
        this.updateChart(combinedPlotData);
        this.dataLoadingSoc = false;
      },
      error: (error) => {
        console.error('Error fetching State of Charge data:', error);
        this.dataLoadingSoc = false;
        this.errorMessageSoc = true;
      }
    });
  }

  initializeColorMap(): void {
    const colors = ['#007BC0', '#18837E','#9E2896'];
    this.selectedVehicles.forEach((vehicle, index) => {
      this.colorMap[vehicle.batteryIdentifier] = colors[index % colors.length];
    });
  }

  plotFleetEndSoc(): any[] {
    const transformXAxisPipe = new TransformXAxisPipe();
    return Object.keys(this.stateOfChargeDatapoints).flatMap((fleetId: string) => {
      const plotData = this.stateOfChargeDatapoints[fleetId];
      const filteredData = plotData.filter((dataObject: any) => dataObject.chargingSessionsEnd !== null);
      const initialSoc = filteredData.map((dataObject: any) => dataObject.binSoc);
      const percentAmount = filteredData.map((dataObject: any) => dataObject.chargingSessionsEnd);
      this.chartRange = transformXAxisPipe.transform(initialSoc, percentAmount, 5,100);
      return {
        x: initialSoc,
        y: percentAmount,
        type: 'bar',
        name: `Fleet`,
        marker: {
          color: '#8A9097'
        }
      };
    });
  }

  updateChart(data: any[]): void {
    const layout = {
      title: 'End State of Charge',
      font: {
        family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
        size: 13,
      },
      barmode: 'group',
      xaxis: {
        title: 'End State of Charge [%]',
        range: this.chartRange?.range
      },
      yaxis: {
        title: 'Percentage of Charging Sessions [%]'
      },
      legend: {
        orientation: 'h',
        y: -0.3,
        xanchor: 'center',
        x: 0.5,
        font: {
          family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
          size: 13,
        },
      }
    };

    createPlotlyChart('end-state-of-charge-ef-chart', data, layout);
  }
}


